import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, ThumbsUp, ThumbsDown, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/hooks/useFetch';

/**
 * Parse trailing `[goto:/path]` token from a bot reply. Returns
 * `{ cleaned, goto }`. If no token is present, `goto` is null. We
 * accept any path that starts with "/" and is < 100 chars, but
 * deliberately reject external URLs and javascript: hrefs to avoid
 * opening anything the model might be tricked into emitting.
 */
const parseGoto = (text) => {
  if (!text) return { cleaned: '', goto: null };
  const re = /\s*\[goto:(\/[a-zA-Z0-9/_\-?=&%.]{1,99})\]\s*$/;
  const m = text.match(re);
  if (!m) return { cleaned: text, goto: null };
  return { cleaned: text.replace(re, '').trim(), goto: m[1] };
};

/**
 * Floating Detective Dino help-bot bubble.
 *
 * - Bottom-right corner, above the BottomNav.
 * - Only visible on user-facing routes (skipped on /login, /register,
 *   /admin* and /auth/callback to avoid clutter).
 * - Persists session_id in localStorage so the conversation survives
 *   reloads.
 * - Uses the actual brand pixel-art icon as the avatar (same image used
 *   in the linktree hub) so it always matches the brand exactly.
 */

const HIDE_ROUTES = ['/login', '/register', '/auth/callback'];
const HIDE_PREFIXES = ['/admin'];
const STORAGE_KEY = 'cca-help-session-id';

const Avatar = ({ size = 28, className = '' }) => (
  <img
    src="/dino-clean.png"
    alt="Detective Dino"
    width={size}
    height={size}
    className={`bg-white rounded-md p-0.5 [image-rendering:pixelated] ${className}`}
    style={{ imageRendering: 'pixelated' }}
  />
);

const formatBotText = (text) => {
  // Lightweight markdown: **bold** and bullet lists. Avoids pulling a full
  // markdown renderer for what is mostly short answers.
  let html = (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n\s*[•\-*]\s+/g, '\n• ')
    .replace(/\n/g, '<br/>');
  return html;
};

export const DetectiveDinoBot = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text:
        "Hi — I'm Detective Dino. Ask me anything about how to use ClearCommunicationApp. I can help you find a feature, save a phrase, change your dialect, manage your subscription, and more.",
    },
  ]);
  const sessionIdRef = useRef(localStorage.getItem(STORAGE_KEY) || null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Hide on auth/admin routes
  const hidden =
    HIDE_ROUTES.includes(location.pathname) ||
    HIDE_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setSending(true);
    import('@/lib/analytics').then(({ track }) => track.helpbotQuestion()).catch(() => {});
    const { data, error } = await apiFetch('/api/help/chat', {
      method: 'POST',
      body: { text, session_id: sessionIdRef.current || undefined },
    });
    setSending(false);
    if (error) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text:
            "I'm having trouble reaching my brain right now. Please try again in a moment, or tap **Feedback** in the menu if it persists.",
        },
      ]);
      return;
    }
    if (data?.session_id) {
      sessionIdRef.current = data.session_id;
      localStorage.setItem(STORAGE_KEY, data.session_id);
    }
    setMessages((m) => {
      const parsed = parseGoto(data.reply);
      return [
        ...m,
        {
          role: 'assistant',
          text: parsed.cleaned,
          goto: parsed.goto,
          message_index: data.message_index,
        },
      ];
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating button - the toggle uses the brand pixel-art avatar so
          users instantly recognize Detective Dino */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open Detective Dino help"
          data-testid="help-bot-toggle"
          className="fixed bottom-24 right-4 z-40 group"
        >
          <span className="absolute inset-0 rounded-full bg-[#00E5CC] opacity-30 animate-ping" />
          <span className="relative h-14 w-14 rounded-full bg-[#00E5CC] hover:bg-[#00C9B3] shadow-xl flex items-center justify-center ring-2 ring-white/40 transition-transform group-hover:scale-105 overflow-hidden">
            <Avatar size={48} />
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-24 right-4 z-50 w-[min(92vw,360px)] h-[min(70vh,540px)] flex flex-col rounded-2xl border bg-card shadow-2xl overflow-hidden"
          data-testid="help-bot-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-[#00E5CC] to-cyan-500 text-[#0d1f2d]">
            <span className="bg-white/20 rounded-full p-1">
              <Avatar size={26} />
            </span>
            <div className="flex-1">
              <p className="font-semibold text-sm leading-tight">Detective Dino</p>
              <p className="text-[11px] opacity-90 leading-tight">
                Help with anything in the app
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="opacity-90 hover:opacity-100"
              aria-label="Close"
              data-testid="help-bot-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-muted/30"
            data-testid="help-bot-messages"
          >
            {/* Quick action: re-trigger the new-user tour. Shown above the
                conversation so users can always find their way back to it,
                no matter how long the thread gets. */}
            <button
              type="button"
              onClick={() => {
                // Tour is now event-driven (manual-only). The single
                // source of truth is the `cca:start-tour` window event;
                // FirstVisitTour listens for it and handles navigation +
                // re-trigger guard reset itself.
                try {
                  // Re-show the homepage bubble for this session too in
                  // case the user dismissed it earlier.
                  sessionStorage.removeItem('cca_take_tour_bubble_dismissed');
                } catch { /* ignore */ }
                setOpen(false);
                window.dispatchEvent(new CustomEvent('cca:start-tour'));
              }}
              data-testid="help-bot-retake-tour"
              className="w-full text-left rounded-xl border-2 border-dashed border-[#00E5CC]/60 bg-gradient-to-br from-[#00E5CC]/10 to-purple-500/10 hover:from-[#00E5CC]/20 hover:to-purple-500/20 px-3 py-2.5 mb-1 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#00A896] group-hover:scale-110 transition-transform" />
                <span className="text-sm font-semibold text-foreground">Take the tour again</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                Replay the new-user walkthrough — Linktree, menu, library, and bottom nav.
              </p>
            </button>

            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {m.role === 'assistant' && (
                  <span className="mr-1.5 mt-1 shrink-0 self-start">
                    <Avatar size={22} />
                  </span>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-[#00E5CC] text-[#0d1f2d] rounded-br-sm'
                      : 'bg-card border rounded-bl-sm'
                  }`}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        m.role === 'assistant'
                          ? formatBotText(m.text)
                          : (m.text || '').replace(/</g, '&lt;'),
                    }}
                  />
                  {m.role === 'assistant' && m.goto && (
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        navigate(m.goto);
                      }}
                      className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#00C9B3] text-[#0d1f2d] hover:bg-[#00B89F] px-3 py-1 text-xs font-semibold transition-colors"
                      data-testid={`help-bot-goto-${m.goto.replace(/[^a-zA-Z0-9]/g, '-')}`}
                    >
                      Open <code className="font-mono opacity-80">{m.goto}</code>
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {/* 👍 / 👎 row sits BELOW the most recent assistant reply only — keeps
                the panel uncluttered while still inviting feedback */}
            {(() => {
              const last = messages[messages.length - 1];
              if (!last || last.role !== 'assistant' || last.message_index == null) return null;
              const idx = messages.length - 1;
              return (
                <div className="flex items-center gap-1 pl-7 mt-1" data-testid="help-bot-feedback-row">
                  <span className="text-[10px] text-muted-foreground mr-1">Helpful?</span>
                  <button
                    type="button"
                    onClick={() => rate(idx, 'up')}
                    disabled={!!last.rating}
                    aria-label="Mark as helpful"
                    data-testid="help-bot-thumbs-up"
                    className={`p-1 rounded hover:bg-muted transition-colors ${
                      last.rating === 'up' ? 'bg-emerald-500/20 text-emerald-700' : 'text-muted-foreground'
                    } disabled:cursor-default`}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => rate(idx, 'down')}
                    disabled={!!last.rating}
                    aria-label="Mark as not helpful"
                    data-testid="help-bot-thumbs-down"
                    className={`p-1 rounded hover:bg-muted transition-colors ${
                      last.rating === 'down' ? 'bg-red-500/20 text-red-700' : 'text-muted-foreground'
                    } disabled:cursor-default`}
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </button>
                  {last.rating && (
                    <span className="text-[10px] text-muted-foreground ml-1">Thanks for the feedback!</span>
                  )}
                </div>
              );
            })()}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground pl-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Detective Dino is thinking…
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t bg-card p-2">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask me anything… (Enter to send)"
                rows={1}
                disabled={sending}
                data-testid="help-bot-input"
                className="flex-1 resize-none rounded-xl border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00E5CC] disabled:opacity-50 max-h-24"
              />
              <Button
                size="sm"
                onClick={send}
                disabled={sending || !input.trim()}
                className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] h-9 w-9 p-0 shrink-0"
                aria-label="Send"
                data-testid="help-bot-send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
              I only help with ClearCommunicationApp · not medical advice
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default DetectiveDinoBot;
